import { captureException, startSpan } from "@sentry/react";

import {
    EntryDTO,
    ICompetitionResponse,
    ICompetitionsResponse,
    IEntryResponse,
} from "../../types";
import { doDelete, doGet, doGetOptAuth, doPostJson } from "../core/http";

export const getCompetition = async (
    competitionId: string
): Promise<ICompetitionResponse> => {
    const url = `/api/competitions/${competitionId}`;
    try {
        const response = await startSpan(
            { name: "getCompetition" },
            async () => {
                return doGetOptAuth(url);
            }
        );
        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }
        return {
            competition: response.data,
        };
    } catch (e) {
        captureException(e);
        return { error: "Failed to get competition." };
    }
};

export const getActiveCompetitions =
    async (): Promise<ICompetitionsResponse> => {
        const url = "/api/competitions/active";
        try {
            const response = await startSpan(
                { name: "getActiveCompetitions" },
                async () => {
                    return doGetOptAuth(url);
                }
            );
            if (response.error) {
                captureException(response.error);
                return { error: response.error };
            }
            return {
                competition: response.data,
            };
        } catch (e) {
            captureException(e);
            return { error: "Failed to get active competitions." };
        }
    };

export const getArchivedCompetitions =
    async (): Promise<ICompetitionsResponse> => {
        const url = "/api/competitions/archived";
        try {
            const response = await startSpan(
                { name: "getArchivedCompetitions" },
                async () => {
                    return doGetOptAuth(url);
                }
            );
            if (response.error) {
                captureException(response.error);
                return { error: response.error };
            }
            return {
                competition: response.data,
            };
        } catch (e) {
            captureException(e);
            return { error: "Failed to get archived competitions." };
        }
    };

export const getEntryByUserAndCompetition = async (
    competitionId: string
): Promise<IEntryResponse | null> => {
    const url = `/api/entries/competition/${competitionId}`;
    try {
        const response = await startSpan(
            { name: "getEntryByUserAndCompetition" },
            async () => {
                return doGet(url, true);
            }
        );
        if (response.error) {
            if (response.error.status === 404) {
                return null;
            } else {
                captureException(response.error);
                return { error: response.error };
            }
        }
        return {
            entry: response.data,
        };
    } catch (e) {
        captureException(e);
        return { error: "Failed to get entry by user and competition." };
    }
};

export const getEligibleCompetitions = async (
    imageId: string
): Promise<ICompetitionsResponse> => {
    const url = `/api/eligibility/image/${imageId}`;
    try {
        const response = await startSpan(
            { name: "getEligibleCompetitions" },
            async () => {
                return doGet(url, true);
            }
        );
        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }
        return {
            competition: response.data,
        };
    } catch (e) {
        captureException(e);
        return { error: "Failed to get eligible competitions." };
    }
};

export const submitEntry = async (
    imageId: string,
    competitionId: string
): Promise<IEntryResponse> => {
    try {
        const entry: EntryDTO = {
            id: "",
            competitionId: competitionId,
            imageId: imageId,
        };
        const response = await startSpan({ name: "submitEntry" }, async () => {
            return doPostJson("/api/entries", true, entry);
        });

        if (response.error) {
            captureException(response.error);
            return { error: response.error };
        }

        return response.data;
    } catch (e) {
        captureException(e);
        return { error: "Failed to submit entry" };
    }
};

export const deleteEntry = async (
    entryId: string
): Promise<{ error?: string }> => {
    const url = `/api/entries/${entryId}`;
    try {
        const { error } = await startSpan({ name: "deleteEntry" }, () => {
            return doDelete(url, true);
        });

        if (error) {
            captureException(error);
            return { error: "Failed to delete entry." };
        }

        return {};
    } catch (e) {
        captureException(e);
        if (e === "User is not logged in.") {
            return { error: e };
        }
        return { error: "Failed to delete entry." };
    }
};
